import { useContext, useState } from "react";
import { Loader2, Phone, Volume2, RefreshCw } from "lucide-react";
import { DataContext } from "@/App";
import { SOSButton } from "@/components/SOSButton";
import { ContactCard } from "@/components/ContactCard";
import { CrowdReportCard } from "@/components/CrowdReportCard";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function SupportView() {
  const { contacts, crowdReports, loading, refreshData } = useContext(DataContext);
  const [localReports, setLocalReports] = useState(null);

  const reports = localReports || crowdReports;

  const handleReportUpdate = (updatedReport) => {
    const updated = reports.map((r) =>
      r.id === updatedReport.id ? updatedReport : r
    );
    setLocalReports(updated);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <Loader2 className="animate-spin text-primary" size={48} />
      </div>
    );
  }

  return (
    <div className="p-4 pb-safe" data-testid="support-view">
      {/* Header with Logo */}
      <header className="mb-6 flex items-center gap-4">
        <img
          src="https://customer-assets.emergentagent.com/job_8e534113-c2c4-4f3f-8864-185da76b04eb/artifacts/d11zdlcb_XK%20Inclusive%20GameDay%20Logo.png"
          alt="XTERMIGATOR Kids Logo"
          className="w-20 h-20 object-contain"
        />
        <div>
          <h1
            className="text-xl font-bold text-foreground"
            style={{ fontFamily: "Lexend, sans-serif" }}
          >
            Support
          </h1>
          <p className="text-xs text-muted-foreground">
            Get help and report crowd conditions
          </p>
          <p className="text-xs text-primary font-medium">XTERMIGATOR Kids</p>
        </div>
      </header>

      {/* SOS Button */}
      <section className="mb-6" aria-label="Emergency assistance">
        <SOSButton />
        <p className="text-xs text-center text-muted-foreground mt-3">
          Sends your location to stadium staff for immediate assistance
        </p>
      </section>

      {/* Tabs */}
      <Tabs defaultValue="contacts" className="w-full">
        <TabsList className="w-full grid grid-cols-2 h-12 rounded-full">
          <TabsTrigger
            value="contacts"
            className="rounded-full h-10 data-[state=active]:bg-primary data-[state=active]:text-white"
            data-testid="tab-contacts"
          >
            <Phone size={16} className="mr-2" />
            Contacts
          </TabsTrigger>
          <TabsTrigger
            value="crowd"
            className="rounded-full h-10 data-[state=active]:bg-primary data-[state=active]:text-white"
            data-testid="tab-crowd"
          >
            <Volume2 size={16} className="mr-2" />
            Crowd Status
          </TabsTrigger>
        </TabsList>

        {/* Contacts Tab */}
        <TabsContent value="contacts" className="mt-4">
          <section aria-label="Contact directory">
            <h2
              className="section-header text-lg"
              style={{ fontFamily: "Lexend, sans-serif" }}
            >
              Quick Contacts
            </h2>
            
            <div className="space-y-3">
              {contacts.length > 0 ? (
                contacts.map((contact) => (
                  <ContactCard key={contact.id} contact={contact} />
                ))
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  No contacts available
                </p>
              )}
            </div>
          </section>
        </TabsContent>

        {/* Crowd Status Tab */}
        <TabsContent value="crowd" className="mt-4">
          <section aria-label="Crowd-sourced noise reports">
            <div className="flex items-center justify-between mb-4">
              <h2
                className="section-header text-lg mb-0"
                style={{ fontFamily: "Lexend, sans-serif" }}
              >
                Live Noise Levels
              </h2>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={refreshData}
                className="h-10 w-10 p-0 rounded-full"
                aria-label="Refresh crowd reports"
                data-testid="refresh-crowd"
              >
                <RefreshCw size={18} />
              </Button>
            </div>
            
            <p className="text-sm text-muted-foreground mb-4">
              Real-time crowd reports from other fans. Vote to confirm accuracy.
            </p>
            
            <div className="space-y-3">
              {reports.length > 0 ? (
                reports.map((report) => (
                  <CrowdReportCard
                    key={report.id}
                    report={report}
                    onUpdate={handleReportUpdate}
                  />
                ))
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  No crowd reports yet
                </p>
              )}
            </div>
          </section>
        </TabsContent>
      </Tabs>
    </div>
  );
}
